import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  MenuItem,
  CircularProgress,
  Grid,
} from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  getLossTypes,
  getEmailTemplate,
  updateEmailTemplate,
} from '../services/claimsApi';
import { LossType } from '../store/claimsSlice';
import { claimColors } from '../styles/claimPageStyles';

const Settings: React.FC = () => {
  const [lossTypes, setLossTypes] = useState<LossType[]>([]);
  const [selectedPerilId, setSelectedPerilId] = useState<string>('');
  const [emailBody, setEmailBody] = useState('');
  const [savedEmailBody, setSavedEmailBody] = useState('');
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const loadLossTypes = async () => {
      try {
        const types = await getLossTypes();
        const filtered = types.filter((t) => t.name !== 'Building Collapse');
        setLossTypes(filtered);
        if (filtered.length > 0) {
          setSelectedPerilId(filtered[0].id);
        }
      } catch {
        setError('Failed to load peril types.');
      } finally {
        setLoadingTypes(false);
      }
    };
    loadLossTypes();
  }, []);

  const loadTemplate = useCallback(async (lossTypeId: string) => {
    setLoadingTemplate(true);
    setError(null);
    try {
      const template = await getEmailTemplate(Number(lossTypeId));
      const body = template.email_body ?? '';
      setEmailBody(body);
      setSavedEmailBody(body);
    } catch {
      setError('Failed to load template for the selected peril.');
      setEmailBody('');
      setSavedEmailBody('');
    } finally {
      setLoadingTemplate(false);
    }
  }, []);

  useEffect(() => {
    if (selectedPerilId !== '') {
      loadTemplate(selectedPerilId);
    }
  }, [selectedPerilId, loadTemplate]);

  const handlePerilChange = (value: string) => {
    setSelectedPerilId(value);
    setSuccess(false);
  };

  const handleUpdate = async () => {
    if (selectedPerilId === '') return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const updated = await updateEmailTemplate(Number(selectedPerilId), emailBody);
      const body = updated.email_body ?? '';
      setEmailBody(body);
      setSavedEmailBody(body);
      setSuccess(true);
    } catch {
      setError('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEmailBody(savedEmailBody);
    setSuccess(false);
    setError(null);
  };

  if (loadingTypes) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: claimColors.navy }} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Template updated successfully.
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'action.hover',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: claimColors.navy }}>
            To Do
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {loadingTemplate ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} sx={{ color: claimColors.navy }} />
            </Box>
          ) : (
            <Box component="form" onSubmit={(e) => e.preventDefault()}>
              <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ pt: 1, textAlign: { sm: 'right' }, pr: { sm: 2 }, fontWeight: 500 }}
                  >
                    Loss Type
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 9, md: 10 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={selectedPerilId}
                    onChange={(e) => handlePerilChange(e.target.value)}
                    sx={{ maxWidth: 360 }}
                  >
                    {lossTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ pt: 1, textAlign: { sm: 'right' }, pr: { sm: 2 }, fontWeight: 500 }}
                  >
                    To Do
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 9, md: 10 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    value={emailBody}
                    onChange={(e) => {
                      setEmailBody(e.target.value);
                      setSuccess(false);
                    }}
                    placeholder="Enter HTML email body for this peril..."
                    sx={{
                      '& .MuiInputBase-input': {
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', gap: 2, pl: { sm: 0, md: '16.666%' } }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleUpdate}
                  disabled={saving || selectedPerilId === ''}
                  sx={{ textTransform: 'none', minWidth: 100 }}
                >
                  {saving ? 'Updating...' : 'Update'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={saving || emailBody === savedEmailBody}
                  startIcon={<RestartAltIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    color: 'text.primary',
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'text.secondary', backgroundColor: 'action.hover' },
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Settings;
